<template>
    <b-modal id="userModal"
             :hide-footer="!userValidated"
             :title="(editUser && editUser.id) ? 'Edit User' : 'Create user'"
             ok-title="Save"
             centered
             size="lg"
             @ok="saveUser"
             @shown="setupUser()"
             @hidden="setupUser()"
    >

        <div v-if="editUser !== null">
            <b-input-group prepend="E-mail address" class="mt-3">
                <b-form-input
                    id="user_email"
                    :state="errors.email === null"
                    @change="errors.email = null"
                    v-model="editUser.email"
                    type="email"
                    required
                    :disabled="userValidated"
                    placeholder="E-mail"
                ></b-form-input>
                <b-input-group-append>
                    <b-button v-if="!userValidated" variant="outline-success" @click="checkForExistingUser">Validate
                    </b-button>
                    <b-button v-else variant="outline-success" @click="userValidated = false">Modify e-mail</b-button>
                </b-input-group-append>

                <template v-if="errors.email !== null">
                    <b-form-invalid-feedback v-for="(error, key) in errors.email" :key="'email_error_' + key">
                        {{ error }}
                    </b-form-invalid-feedback>
                </template>
            </b-input-group>

            <template v-if="userValidated">
                <b-input-group prepend="Name" class="mt-3">
                    <b-form-input
                        id="user_name"
                        :state="errors.name === null"
                        @change="errors.name = null"
                        v-model="editUser.name"
                        type="text"
                        required
                        autocomplete="name"
                        placeholder="Name"
                    ></b-form-input>

                    <template v-if="errors.name !== null">
                        <b-form-invalid-feedback v-for="(error, key) in errors.name" :key="'name_error_' + key">
                            {{ error }}
                        </b-form-invalid-feedback>
                    </template>
                </b-input-group>

                <b-input-group prepend="Role" class="mt-3">
                    <b-form-select
                        :state="errors['role.id'] === null"
                        @change="onRoleChange"
                        class="form-control"
                        placeholder="Role"
                        v-model="editUser.role.id"
                        required>
                        <b-form-select-option v-for="(role, key) in availableRoles" :key="'role_' + role.id + '_' + key"
                                              :value="role.id" :disabled="!!role.disabled">
                            {{ role.name }}<template v-if="role.disabled"> (limit reached)</template>
                        </b-form-select-option>
                    </b-form-select>

                    <template v-if="errors['role.id'] !== null">
                        <b-form-invalid-feedback v-for="(error, key) in errors['role.id']" :key="'role_error_' + key">
                            {{ error }}
                        </b-form-invalid-feedback>
                    </template>
                </b-input-group>

                <b-input-group prepend="Date added" class="mt-3" v-if="isReseller">
                    <b-form-datepicker
                        id="user_date_added"
                        v-model="editUser.date_added"
                        placeholder="Date added"
                        locale="fi"
                    ></b-form-datepicker>
                </b-input-group>

                <div class="input-group mt-3">
                    <verte :picker="'wheel'" model="rgb"
                           :state="errors.color === null"
                           @change="errors.color = null"
                           v-model="editUser.color" :model="'hex'" :enableAlpha="false"
                           :rgbSliders="false"></verte>
                    &nbsp;
                    <input type="text" placeholder="Color" class="form-control" required
                           v-model="editUser.color" disabled/>

                    <template v-if="errors.color !== null">
                        <b-form-invalid-feedback v-for="(error, key) in errors.color" :key="'color_error' + key"
                                                 force-show>
                            {{ error }}
                        </b-form-invalid-feedback>
                    </template>
                </div>

                <hr/>
                <template v-if="isCompanyMember">
                    <b-button variant="outline-danger" :disabled="sendingResetLink" @click="sendPasswordResetLink">
                        Send password reset link
                    </b-button>
                    <p class="mt-2 mb-0 text-muted">
                        The user receives an e-mail with a link to set a new password. The link is valid for 2 days.
                    </p>
                </template>
                <p class="mb-0 text-muted" v-else-if="editUser.id">
                    This person already has a DOTAG account and keeps their current password.
                </p>
                <p class="mb-0 text-muted" v-else>
                    The user will receive an e-mail with a link to set their password. The link is valid for 2 days.
                </p>
            </template>
        </div>
    </b-modal>
</template>

<script>
import {cloneDeep} from "lodash";
import axios from "axios";

import Verte from 'verte';
import 'verte/dist/verte.css';

export default {
    components: {Verte},
    name: "UserModal",
    props: {
        user: {
            type: Object,
            required: false,
            default: null
        },
        isReseller: {
            type: Boolean,
            required: false,
            default: true
        },
        validateUserLimits: {
            type: Boolean,
            required: false,
            default: false
        },
        company: {
            type: Object,
            required: false,
            default: null
        },
        currentUsers: {
            type: Array,
            required: false,
            default: () => []
        },
        outsideUsers: {
            type: Array,
            required: false,
            default: () => []
        },
        outsideRoleIds: {
            type: Array,
            required: false,
            default: () => [6]
        },
        // The editing user's own role in this company, when the parent knows it.
        ownRoleId: {
            type: Number,
            required: false,
            default: null
        }
    },
    data() {
        return {
            editUser: null,
            userValidated: false,
            roleArray: [
                {name: 'Outside inspector', id: 6, is_unique: false},
                {name: 'Subcontractor', id: 5, is_unique: false},
                {name: 'User', id: 4, is_unique: true},
                {name: 'Project Manager', id: 3, is_unique: true},
                {name: 'Company Manager', id: 2, is_unique: true},
                {name: 'Admin', id: 1, is_unique: true},
            ],
            sendingResetLink: false,
            errors: {
                email: null,
                name: null,
                'role.id': null,
                color: null
            }
        };
    },
    methods: {
        setupUser() {
            this.editUser = cloneDeep(this.user);
            this.userValidated = !!(this.editUser && this.editUser.id);
            this.sendingResetLink = false;
            this.errors = {
                email: null,
                name: null,
                'role.id': null,
                color: null
            };
        },

        // Confirmation popup; on the root so it survives this dialog closing.
        notify(title, message) {
            return this.$root.$bvModal.msgBoxOk(message, {title: title, centered: true, okVariant: 'danger'});
        },

        sendPasswordResetLink() {
            this.sendingResetLink = true;

            axios({
                url: this.$store.state.apiURL + 'companies/' + this.$route.params.company_id + '/users/' + this.editUser.id + '/send-password-reset',
                method: 'POST',
                headers: {"Authorization": "Bearer " + this.$store.state.token}
            })
                .then(response => {
                    this.notify(
                        'Password reset e-mail sent',
                        'A password reset e-mail was sent to ' + this.editUser.email + '. The link is valid for 2 days.'
                    );
                })
                .catch(error => {
                    this.notify(
                        'E-mail not sent',
                        error.response?.data?.message || 'Could not send the password reset link.'
                    );
                })
                .finally(() => {
                    this.sendingResetLink = false;
                });
        },

        validateUserLimitsForRole(roleId) {
            if (!this.validateUserLimits || !this.company) {
                return true;
            }

            const isOutsideRole = this.outsideRoleIds.includes(roleId);

            if (this.editUser && this.editUser.id) {
                // Compare against the saved role, not the one currently picked in the select.
                const savedUser = [...this.currentUsers, ...this.outsideUsers].find(user => user.id === this.editUser.id);

                if (savedUser) {
                    // Deleted users hold no slot until restored; same budget means no new slot is taken.
                    if (savedUser.is_deleted || this.outsideRoleIds.includes(savedUser.role.id) === isOutsideRole) {
                        return true;
                    }
                }
            }

            const currentCount = (isOutsideRole ? this.outsideUsers : this.currentUsers)
                .filter(user => !user.is_deleted).length;

            const maxCount = isOutsideRole ?
                (this.company.max_number_of_outside_users || 0) :
                (this.company.max_number_of_users || 0);

            return currentCount < maxCount;
        },


        onRoleChange() {
            this.errors['role.id'] = null;

            if (this.editUser.role.id && !this.validateUserLimitsForRole(this.editUser.role.id)) {
                const groupType = this.outsideRoleIds.includes(this.editUser.role.id) ?
                    'outside users' : 'users';
                this.errors['role.id'] = [`Maximum number of ${groupType} reached`];
                this.editUser.role.id = null;
            }
        },

        validateEmail(mail) {
            return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail);
        },

        checkForExistingUser() {
            if (this.editUser.email && this.validateEmail(this.editUser.email)) {
                let token = this.$store.state.token;

                const method = 'POST';
                const url = this.$store.state.apiURL + 'companies/' + this.$route.params.company_id + '/users/get-by-email';

                const data = {
                    email: this.editUser.email
                };

                axios({
                    url: url,
                    data: data,
                    method: method,
                    headers: {"Authorization": "Bearer " + token}
                })
                    .then(response => {
                        if (response.data.data && response.data.data.id) {
                            this.editUser = response.data.data;
                        }

                        this.userValidated = true;

                    })
                    .catch((error) => {
                        if (error.response.data.message) {
                            if (this.editUser.id && this.user.email === this.editUser.email) {
                                this.editUser = cloneDeep(this.user);
                                this.userValidated = true;
                            } else {
                                this.errors.email = [error.response.data.message];
                            }
                        } else {
                            this.errors.email = ['Something went wrong while validating the email. Please try again.'];
                        }
                    });
            } else {
                this.errors.email = ['Invalid email address'];
            }
        },
        saveUser(bvModalEvt) {
            bvModalEvt.preventDefault();

            // Validate user limits
            if (!this.isReseller && this.validateUserLimits && !this.validateUserLimitsForRole(this.editUser.role.id)) {
                const groupType = this.outsideRoleIds.includes(this.editUser.role.id) ?
                    'outside users' : 'users';
                this.errors['role.id'] = [`Maximum number of ${groupType} reached`];
                return;
            }

            let token = this.$store.state.token;

            let method;
            let url = this.$store.state.apiURL + 'companies/' + this.$route.params.company_id + '/users';

            if (this.editUser.id) {
                url += '/' + this.editUser.id;
                method = 'PUT';
            } else {
                method = 'POST';
            }


            let data = this.editUser;

            // Decide the confirmation before the dialog resets its state.
            const email = this.editUser.email;
            const isNewAccount = !this.editUser.id;
            const addedExistingAccount = !isNewAccount && !this.isCompanyMember;

            axios({
                url: url,
                data: data,
                method: method,
                headers: {"Authorization": "Bearer " + token}
            })
                .then(response => {
                    if (isNewAccount) {
                        if (response.data.password_link_sent === false) {
                            this.notify(
                                'User created, e-mail not sent',
                                'The user was created, but the e-mail with the password link could not be sent. Open the user and press "Send password reset link" to try again.'
                            );
                        } else {
                            this.notify(
                                'User created',
                                'An e-mail with a link to set the password was sent to ' + email + '. The link is valid for 2 days.'
                            );
                        }
                    } else if (addedExistingAccount) {
                        this.notify(
                            'User added',
                            email + ' already has a DOTAG account, so no e-mail was sent. They sign in with their current password.'
                        );
                    }

                    this.$emit('userSaved');
                })
                .catch((error, data) => {
                    const responseData = error.response.data;
                    if (typeof responseData.errors !== 'undefined') {

                        for (const [key, error] of Object.entries(responseData.errors)) {
                            const messages = (Array.isArray(error) ? error : [error])
                                .map(message => String(message).replace(/<[^>]+>/g, ''));

                            // Limit errors have no field of their own, show them at the role select.
                            this.errors[key === 'user_limit_exceeded' ? 'role.id' : key] = messages;
                        }
                    } else if (typeof responseData.message !== 'undefined') {
                        alert(responseData.message);
                    } else {
                        alert('Something went wrong');
                    }
                });
        }
    },
    computed: {
        // Editing someone who is already in this company (not a new user, and not an
        // existing account that is only now being added through the e-mail lookup).
        isCompanyMember() {
            return !!(this.user && this.user.id && this.editUser && this.editUser.id === this.user.id);
        },


        availableRoles() {
            if (this.isReseller) {
                return this.roleArray;
            }
            // Non-reseller: only own level and below; roles whose budget is full stay visible but disabled.
            return this.roleArray
                .filter(role => role.id >= (this.ownRoleId ?? this.userCompany?.role ?? 9999))
                .map(role => ({
                    ...role,
                    disabled: this.validateUserLimits && !this.validateUserLimitsForRole(role.id)
                }));
        },

        userCompany() {
            return this.getUserCompany?.(this.$store.state.company.id);
        }
    }
}
</script>
